// CHANGE: Removed all debug console statements for production
// Import result modal for displaying CSV import results
// Shows created users with passwords, new sites, and errors
// Updated to support 5 site columns instead of semicolon-separated string

import { createPortal } from 'react-dom';
import { getPortalRoot } from '../../lib/portalUtils';
import { X, Download, Copy, Check, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { downloadResultsCSV } from '../../lib/csvImport';

export interface ImportResult {
  username: string;
  firstName: string;
  lastName: string;
  role: string;
  site1?: string;
  site2?: string;
  site3?: string;
  site4?: string;
  site5?: string;
  password?: string;
  error?: string;
}

interface ImportResultModalProps {
  results: ImportResult[];
  newSites: string[];
  onClose: () => void;
  onImportComplete?: () => void;
}

export function ImportResultModal({ results, newSites, onClose, onImportComplete }: ImportResultModalProps) {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);

  const successfulUsers = results.filter(r => !r.error);
  const failedUsers = results.filter(r => r.error);

  const handleCopyPassword = async (password: string, index: number) => {
    try {
      await navigator.clipboard.writeText(password);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (error) {
    }
  };

  const handleCopyAll = async () => {
    try {
      const content = successfulUsers
        .map(u => `${u.username}: ${u.password}`)
        .join('\n');
      await navigator.clipboard.writeText(content);
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    } catch (error) {
    }
  };

  const handleDownload = () => {
    downloadResultsCSV(results);
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose, onImportComplete]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleClose = () => {
    onClose();
    // Call onImportComplete AFTER closing the modal, not before
    if (onImportComplete) {
      onImportComplete();
    }
  };

  return createPortal(
    <div className="modal-overlay-above" onClick={handleOverlayClick} role="dialog" aria-modal="true" aria-labelledby="import-result-title">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 shrink-0">
          <div>
            <h3 id="import-result-title" className="text-xl font-bold text-gray-900">Résultats de l'import</h3>
            <p className="text-sm text-gray-600 mt-1">
              {successfulUsers.length} utilisateur{successfulUsers.length > 1 ? 's' : ''} créé{successfulUsers.length > 1 ? 's' : ''}
              {failedUsers.length > 0 && `, ${failedUsers.length} erreur${failedUsers.length > 1 ? 's' : ''}`}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* New Sites Section */}
          {newSites.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                <div className="flex-1">
                  <p className="font-semibold text-blue-900 mb-2">
                    {newSites.length} nouveau{newSites.length > 1 ? 'x' : ''} site{newSites.length > 1 ? 's' : ''} créé{newSites.length > 1 ? 's' : ''}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {newSites.map((site, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300"
                      >
                        {site}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Success Section */}
          {successfulUsers.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <CheckCircle className="text-green-600" size={20} />
                  Utilisateurs créés avec succès
                </h4>
                <div className="flex gap-2">
                  <button
                    onClick={handleCopyAll}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-all duration-200"
                  >
                    {copiedAll ? (
                      <>
                        <Check size={16} />
                        <span>Copié</span>
                      </>
                    ) : (
                      <>
                        <Copy size={16} />
                        <span>Copier tout</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleDownload}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-all duration-200"
                  >
                    <Download size={16} />
                    <span>Télécharger CSV</span>
                  </button>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
                  <div className="text-sm text-yellow-800">
                    <p className="font-semibold mb-1">Important !</p>
                    <p>Ces mots de passe temporaires ne seront plus affichés. Assurez-vous de les télécharger ou de les copier maintenant.</p>
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Nom d'utilisateur</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Nom complet</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Rôle</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Sites</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Mot de passe</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase w-20"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {successfulUsers.map((user, index) => {
                        const userSites = [user.site1, user.site2, user.site3, user.site4, user.site5]
                          .filter(site => site && site.length > 0);
                        const sitesDisplay = userSites.length > 0 ? userSites.join(', ') : '-';

                        return (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-semibold text-gray-900">{user.username}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{user.firstName} {user.lastName}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{user.role}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{sitesDisplay}</td>
                            <td className="px-4 py-3 text-sm">
                              <span className="font-mono text-gray-900 bg-gray-100 px-2 py-1 rounded">{user.password}</span>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <button
                                onClick={() => handleCopyPassword(user.password || '', index)}
                                className="p-2 hover:bg-gray-100 rounded transition-colors"
                                title="Copier le mot de passe"
                              >
                                {copiedIndex === index ? (
                                  <Check size={16} className="text-green-600" />
                                ) : (
                                  <Copy size={16} className="text-gray-600" />
                                )}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Error Section */}
          {failedUsers.length > 0 && (
            <div>
              <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                <XCircle className="text-red-600" size={20} />
                Erreurs rencontrées
              </h4>

              <div className="border border-red-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-red-50 border-b border-red-200">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-red-900 uppercase">Nom d'utilisateur</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-red-900 uppercase">Nom complet</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-red-900 uppercase">Rôle</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-red-900 uppercase">Erreur</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-red-100">
                      {failedUsers.map((user, index) => (
                        <tr key={index} className="bg-white hover:bg-red-50">
                          <td className="px-4 py-3 text-sm font-semibold text-gray-900">{user.username}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{user.firstName} {user.lastName}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{user.role}</td>
                          <td className="px-4 py-3 text-sm text-red-700">{user.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 shrink-0">
          <button
            onClick={handleClose}
            className="w-full px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all duration-200"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>,
    getPortalRoot()
  );
}
